/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.28/esri/copyright.txt for details.
*/
import { _ as e } from "../../chunks/tslib.es6.js";
import { geometryTypes as t } from "../../geometry.js";
import r from "../../Graphic.js";
import { JSONMap as o } from "../../core/jsonMap.js";
import { JSONSupport as s } from "../../core/JSONSupport.js";
import { clone as n } from "../../core/lang.js";
import { property as i } from "../../core/accessorSupport/decorators/property.js";
import "../../core/accessorSupport/ensureType.js";
import { reader as l } from "../../core/accessorSupport/decorators/reader.js";
import { subclass as a } from "../../core/accessorSupport/decorators/subclass.js";
import { writer as p } from "../../core/accessorSupport/decorators/writer.js";
import m from "../../geometry/SpatialReference.js";
import { fromJSON as c, isPolygon as u } from "../../geometry/support/jsonUtils.js";
import y from "../../layers/support/Field.js";
var f;
const h = new o({
  esriGeometryPoint: "point",
  esriGeometryMultipoint: "multipoint",
  esriGeometryPolyline: "polyline",
  esriGeometryPolygon: "polygon",
  esriGeometryEnvelope: "extent",
  mesh: "mesh",
  "": null
});
let g = f = class extends s {
  constructor(e) {
    super(e), this.displayFieldName = null, this.exceededTransferLimit = !1, this.features = [], this.fields = null, this.geometryType = null, this.hasM = !1, this.hasZ = !1, this.queryGeometry = null, this.spatialReference = null;
  }
  readFeatures(e, t) {
    const o = m.fromJSON(t.spatialReference),
      s = [];
    for (let n = 0; n < e.length; n++) {
      const t = e[n],
        i = r.fromJSON(t),
        l = t.geometry?.spatialReference;
      null == i.geometry || l || (i.geometry.spatialReference = o);
      const a = t.aggregateGeometries,
        p = i.aggregateGeometries;
      if (a && null != p) for (const e in p) {
        const t = p[e],
          r = a[e],
          s = r?.spatialReference;
        null == t || s || (t.spatialReference = o);
      }
      s.push(i);
    }
    return s;
  }
  writeGeometryType(e, t, r, o) {
    if (e) return void h.write(e, t, r, o);
    const {
      features: s
    } = this;
    if (s) for (const n of s) if (null != n?.geometry) return void h.write(n.geometry.type, t, r, o);
  }
  readQueryGeometry(e, t) {
    if (!e) return null;
    const r = !!e.spatialReference,
      o = c(e);
    return o && !r && t.spatialReference && (o.spatialReference = m.fromJSON(t.spatialReference)), o;
  }
  writeSpatialReference(e, t) {
    if (e) return void (t.spatialReference = e.toJSON());
    const {
      features: r
    } = this;
    if (r) for (const o of r) if (o && null != o.geometry && o.geometry.spatialReference) return void (t.spatialReference = o.geometry.spatialReference.toJSON());
  }
  clone() {
    return new f(this.cloneProperties());
  }
  cloneProperties() {
    return n({
      displayFieldName: this.displayFieldName,
      exceededTransferLimit: this.exceededTransferLimit,
      features: this.features,
      fields: this.fields,
      geometryType: this.geometryType,
      hasM: this.hasM,
      hasZ: this.hasZ,
      queryGeometry: this.queryGeometry,
      spatialReference: this.spatialReference,
      transform: this.transform
    });
  }
  toJSON(e) {
    const t = this.write();
    if (t.features && Array.isArray(e) && e.length > 0) for (let r = 0; r < t.features.length; r++) {
      const o = t.features[r];
      if (o.geometry) {
        const t = e?.[r];
        o.geometry = t?.toJSON() || o.geometry;
      }
    }
    return t;
  }
  quantize(e) {
    const {
        scale: [t, r],
        translate: [o, s]
      } = e,
      n = e => Math.round((e - o) / t),
      i = e => Math.round((s - e) / r),
      l = this.features,
      a = this._getQuantizationFunction(this.geometryType, n, i);
    for (let p = 0, m = l.length; p < m; p++) a?.(l[p].geometry) || (l.splice(p, 1), p--, m--);
    return this.transform = e, this;
  }
  unquantize() {
    const {
      geometryType: e,
      features: t,
      transform: r
    } = this;
    if (!r) return this;
    const {
        translate: [o, s],
        scale: [n, i]
      } = r,
      l = e => e * n + o,
      a = e => s - e * i;
    let p = null,
      m = null;
    if (this.hasZ && null != r?.scale?.[2]) {
      const {
        translate: [,, e],
        scale: [,, t]
      } = r;
      p = r => r * t + e;
    }
    if (this.hasM && null != r?.scale?.[3]) {
      const {
        translate: [,,, e],
        scale: [,,, t]
      } = r;
      m = r => null == r ? r : r * t + e;
    }
    const c = this._getHydrationFunction(e, l, a, p, m);
    for (const {
      geometry: u
    } of t) null != u && c && c(u);
    return this.transform = null, this;
  }
  _quantizePoints(e, t, r) {
    let o, s;
    const n = [];
    for (let i = 0, l = e.length; i < l; i++) {
      const l = e[i];
      if (i > 0) {
        const e = t(l[0]),
          i = r(l[1]);
        e === o && i === s || (n.push([e - o, i - s]), o = e, s = i);
      } else o = t(l[0]), s = r(l[1]), n.push([o, s]);
    }
    return n.length > 0 ? n : null;
  }
  _getQuantizationFunction(e, t, r) {
    return "point" === e ? e => (e.x = t(e.x), e.y = r(e.y), e) : "polyline" === e || "polygon" === e ? e => {
      const o = u(e) ? e.rings : e.paths,
        s = [];
      for (let n = 0, i = o.length; n < i; n++) {
        const e = o[n],
          i = this._quantizePoints(e, t, r);
        i && s.push(i);
      }
      return s.length > 0 ? (u(e) ? e.rings = s : e.paths = s, e) : null;
    } : "multipoint" === e ? e => {
      const o = this._quantizePoints(e.points, t, r);
      return o && o.length > 0 ? (e.points = o, e) : null;
    } : "extent" === e ? e => e : null;
  }
  _getHydrationFunction(e, t, r, o, s) {
    return "point" === e ? e => {
      e.x = t(e.x), e.y = r(e.y), o && (e.z = o(e.z));
    } : "polyline" === e || "polygon" === e ? e => {
      const n = u(e) ? e.rings : e.paths;
      let i, l;
      for (let o = 0, s = n.length; o < s; o++) {
        const e = n[o];
        for (let o = 0, s = e.length; o < s; o++) {
          const s = e[o];
          o > 0 ? (i += s[0], l += s[1]) : (i = s[0], l = s[1]), s[0] = t(i), s[1] = r(l);
        }
      }
      if (o && s) for (let t = 0, r = n.length; t < r; t++) {
        const e = n[t];
        for (let t = 0, r = e.length; t < r; t++) {
          const r = e[t];
          r[2] = o(r[2]), r[3] = s(r[3]);
        }
      } else if (o) for (let t = 0, r = n.length; t < r; t++) {
        const e = n[t];
        for (let t = 0, r = e.length; t < r; t++) {
          const r = e[t];
          r[2] = o(r[2]);
        }
      } else if (s) for (let t = 0, r = n.length; t < r; t++) {
        const e = n[t];
        for (let t = 0, r = e.length; t < r; t++) {
          const r = e[t];
          r[2] = s(r[2]);
        }
      }
    } : "extent" === e ? e => {
      e.xmin = t(e.xmin), e.ymin = r(e.ymin), e.xmax = t(e.xmax), e.ymax = r(e.ymax), o && null != e.zmax && null != e.zmin && (e.zmax = o(e.zmax), e.zmin = o(e.zmin)), s && null != e.mmax && null != e.mmin && (e.mmax = s(e.mmax), e.mmin = s(e.mmin));
    } : "multipoint" === e ? e => {
      const n = e.points;
      let i, l;
      for (let o = 0, s = n.length; o < s; o++) {
        const e = n[o];
        o > 0 ? (i += e[0], l += e[1]) : (i = e[0], l = e[1]), e[0] = t(i), e[1] = r(l);
      }
      if (o && s) for (let t = 0, r = n.length; t < r; t++) {
        const e = n[t];
        e[2] = o(e[2]), e[3] = s(e[3]);
      } else if (o) for (let t = 0, r = n.length; t < r; t++) {
        const e = n[t];
        e[2] = o(e[2]);
      } else if (s) for (let t = 0, r = n.length; t < r; t++) {
        const e = n[t];
        e[2] = s(e[2]);
      }
    } : null;
  }
};
e([i({
  type: String,
  json: {
    write: !0
  }
})], g.prototype, "displayFieldName", void 0), e([i({
  type: Boolean,
  json: {
    write: {
      overridePolicy: e => ({
        enabled: e
      })
    }
  }
})], g.prototype, "exceededTransferLimit", void 0), e([i({
  type: [r],
  json: {
    write: !0
  }
})], g.prototype, "features", void 0), e([l("features")], g.prototype, "readFeatures", null), e([i({
  type: [y],
  json: {
    write: !0
  }
})], g.prototype, "fields", void 0), e([i({
  type: ["point", "multipoint", "polyline", "polygon", "extent", "mesh"],
  json: {
    read: {
      reader: h.read
    }
  }
})], g.prototype, "geometryType", void 0), e([p("geometryType")], g.prototype, "writeGeometryType", null), e([i({
  type: Boolean,
  json: {
    write: {
      overridePolicy: e => ({
        enabled: e
      })
    }
  }
})], g.prototype, "hasM", void 0), e([i({
  type: Boolean,
  json: {
    write: {
      overridePolicy: e => ({
        enabled: e
      })
    }
  }
})], g.prototype, "hasZ", void 0), e([i({
  types: t,
  json: {
    write: !0
  }
})], g.prototype, "queryGeometry", void 0), e([l("queryGeometry")], g.prototype, "readQueryGeometry", null), e([i({
  type: m,
  json: {
    write: !0
  }
})], g.prototype, "spatialReference", void 0), e([p("spatialReference")], g.prototype, "writeSpatialReference", null), e([i({
  json: {
    write: !0
  }
})], g.prototype, "transform", void 0), g = f = e([a("esri.rest.support.FeatureSet")], g), g.prototype.toJSON.isDefaultToJSON = !0;
const d = g;
export { d as default };